import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  CreditCard,
  FileText,
  TrendingUp,
  BarChart3,
  LineChart,
  PieChart,
} from "lucide-react";
import { getDashboardChartStats } from "../services/dashboardChartService";
import { tData } from "../i18n";
import { usePanelTheme } from "../context/themeContext";

function formatMoney(value, currency = "EUR") {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function formatLabel(monthKey) {
  if (!monthKey) return "";
  const [year, month] = String(monthKey).split("-");
  return `${month}/${year?.slice(2)}`;
}

function maxValue(rows, keys) {
  const values = rows.flatMap((row) =>
    keys.map((key) => Math.abs(Number(row[key] || 0)))
  );
  return Math.max(0, ...values);
}

function buildLineAndAreaPath(values, width, height, max) {
  if (!values.length || !max) return { line: "", area: "" };

  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const linePath = values
    .map((value, index) => {
      const x = index * stepX;
      const y = height - (Math.abs(Number(value || 0)) / max) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(" ");

  const areaPath = `${linePath} L ${width.toFixed(2)} ${height} L 0 ${height} Z`;

  return { line: linePath, area: areaPath };
}

function MiniStat({ label, value, tone = "slate", icon: Icon, theme }) {
  const tones = {
    slate:
      theme === "dark"
        ? "bg-slate-950 text-slate-100 border-slate-700 hover:border-slate-500"
        : "bg-white text-slate-800 border-slate-200 hover:border-slate-300",

    emerald:
      theme === "dark"
        ? "bg-slate-950 text-emerald-300 border-emerald-800 hover:border-emerald-600"
        : "bg-white text-emerald-800 border-emerald-200 hover:border-emerald-300",

    blue:
      theme === "dark"
        ? "bg-slate-950 text-blue-300 border-blue-800 hover:border-blue-600"
        : "bg-white text-blue-800 border-blue-200 hover:border-blue-300",

    amber:
      theme === "dark"
        ? "bg-slate-950 text-amber-300 border-amber-800 hover:border-amber-600"
        : "bg-white text-amber-800 border-amber-200 hover:border-amber-300",
  };

  const badgeTones = {
    slate:
      theme === "dark"
        ? "bg-slate-800 text-slate-200"
        : "bg-slate-100 text-slate-600",
    emerald:
      theme === "dark"
        ? "bg-emerald-900/40 text-emerald-300"
        : "bg-emerald-100 text-emerald-600",
    blue:
      theme === "dark"
        ? "bg-blue-900/40 text-blue-300"
        : "bg-blue-100 text-blue-600",
    amber:
      theme === "dark"
        ? "bg-amber-900/40 text-amber-300"
        : "bg-amber-100 text-amber-600",
  };

  return (
    <div
      className={`group min-w-0 flex flex-col justify-between rounded-2xl border px-3 py-3 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md sm:px-5 sm:py-4 ${tones[tone] || tones.slate}`}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-105 sm:h-10 sm:w-10 ${
            badgeTones[tone] || badgeTones.slate
          }`}
        >
          {Icon && <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.2} />}
        </div>

        <div
          className={`min-w-0 truncate text-[10px] font-extrabold uppercase tracking-[0.14em] sm:text-xs sm:tracking-[0.18em] ${
            theme === "dark"
              ? "text-slate-300 group-hover:text-white"
              : "text-slate-500 group-hover:text-slate-800"
          }`}
        >
          {label}
        </div>
      </div>

      <div className="mt-2 min-w-0 break-words pl-12 text-lg font-black tracking-tight sm:pl-0 sm:text-xl">
        {value}
      </div>
    </div>
  );
}

function EmptyChartState({ title, description, icon: Icon = BarChart3, theme }) {
  return (
    <div
      className={`flex h-[210px] sm:h-[240px] items-center justify-center rounded-2xl sm:rounded-[24px] border border-dashed text-center ${
        theme === "dark"
          ? "border-slate-700 bg-slate-950/60"
          : "border-slate-200 bg-slate-50/50"
      }`}
    >
      <div className="flex flex-col items-center">
        <div
          className={`mb-3 rounded-full p-3 ${
            theme === "dark"
              ? "bg-slate-800 text-slate-300"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          <Icon className="h-6 w-6" />
        </div>

        <div
          className={`text-sm sm:text-base font-black ${
            theme === "dark" ? "text-slate-100" : "text-slate-800"
          }`}
        >
          {title}
        </div>

        <div
          className={`mt-2 max-w-[240px] text-xs sm:text-sm leading-5 px-4 ${
            theme === "dark" ? "text-slate-300" : "text-slate-500"
          }`}
        >
          {description}
        </div>
      </div>
    </div>
  );
}

export default function DashboardCharts({ language = "de" }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const { currentTheme, theme } = usePanelTheme();

  const langPack = tData?.[language] || tData?.de || {};

  const labels = {
    cashFlowTitle: langPack.dashboardCashFlowTitle || "Cash Flow",
    cashFlowSubtitle:
      langPack.dashboardCashFlowSubtitle ||
      "Monthly breakdown of money in and out.",
    invoicedVsResultTitle:
      langPack.dashboardInvoicedVsResultTitle || "Invoices vs Result",
    invoicedVsResultSubtitle:
      langPack.dashboardInvoicedVsResultSubtitle ||
      "Monthly comparison between invoiced and result.",
    incomeShort: langPack.dashboardIncomeShort || langPack.income || "Income",
    expensesShort:
      langPack.dashboardExpensesShort || langPack.expenses || "Expenses",
    paymentsShort:
      langPack.dashboardPaymentsShort || langPack.payments || "Payments",
    resultShort:
      langPack.dashboardResultShort || langPack.dashboardProfit || "Result",
    noChartData:
      langPack.dashboardNoChartData || "No chart data yet for this period.",
    loading: langPack.loading || "Loading...",
    invoiced: langPack.dashboardInvoiced || "Invoiced",
  };

  const loadData = async (force = false) => {
    setLoading(true);
    try {
      const data = await getDashboardChartStats({ force, months: 12 });
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Gabim gjatë ngarkimit të grafikëve:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(false);
  }, []);

  const barMax = useMemo(
    () => maxValue(rows, ["income", "expenses", "payments"]),
    [rows]
  );
  const lineMax = useMemo(
    () => maxValue(rows, ["invoiced", "result"]),
    [rows]
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.income += Number(row.income || 0);
        acc.expenses += Number(row.expenses || 0);
        acc.payments += Number(row.payments || 0);
        acc.invoiced += Number(row.invoiced || 0);
        acc.result += Number(row.result || 0);
        return acc;
      },
      { income: 0, expenses: 0, payments: 0, invoiced: 0, result: 0 }
    );
  }, [rows]);

  const lineWidth = 380;
  const lineHeight = 140;
  const invoicedPaths = buildLineAndAreaPath(
    rows.map((r) => Number(r.invoiced || 0)),
    lineWidth,
    lineHeight,
    lineMax
  );
  const resultPaths = buildLineAndAreaPath(
    rows.map((r) => Number(r.result || 0)),
    lineWidth,
    lineHeight,
    lineMax
  );

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <div
        className={`flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md sm:rounded-[32px] sm:p-6 ${currentTheme.surface}`}
      >
        <div className="mb-5 flex min-w-0 items-start justify-between gap-3 sm:mb-6">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-black sm:text-2xl">
              {labels.cashFlowTitle}
            </h3>
            <p className={`mt-1 text-sm leading-6 sm:text-base ${currentTheme.muted}`}>
              {labels.cashFlowSubtitle}
            </p>
          </div>
        </div>

        {loading ? (
          <div className={`flex flex-1 items-center justify-center text-sm sm:text-base font-semibold ${currentTheme.muted}`}>
            {labels.loading}
          </div>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 sm:mb-6">
              <MiniStat
                label={labels.incomeShort}
                value={formatMoney(totals.income)}
                tone="emerald"
                icon={ArrowUpRight}
                theme={theme}
              />
              <MiniStat
                label={labels.expensesShort}
                value={formatMoney(totals.expenses)}
                tone="amber"
                icon={ArrowDownRight}
                theme={theme}
              />
              <MiniStat
                label={labels.paymentsShort}
                value={formatMoney(totals.payments)}
                tone="blue"
                icon={CreditCard}
                theme={theme}
              />
              <MiniStat
                label={labels.resultShort}
                value={formatMoney(totals.result)}
                tone="slate"
                icon={Wallet}
                theme={theme}
              />
            </div>

            {!barMax ? (
              <EmptyChartState
                title={labels.cashFlowTitle}
                description={labels.noChartData}
                icon={PieChart}
                theme={theme}
              />
            ) : (
              <div className="mt-auto min-w-0">
                <div className="w-full overflow-hidden">
                  <div
                    className={`relative flex h-[180px] items-end gap-1 rounded-2xl border p-3 pt-8 sm:h-[240px] sm:gap-2 sm:rounded-3xl sm:p-4 sm:pt-10 ${
                      theme === "dark"
                        ? "border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950"
                        : "border-slate-100 bg-gradient-to-b from-white to-slate-50/50"
                    }`}
                  >
                    <div className="pointer-events-none absolute inset-x-3 inset-y-8 flex flex-col justify-between sm:inset-x-4 sm:inset-y-10">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`w-full border-t ${
                            theme === "dark"
                              ? "border-slate-800/90"
                              : "border-slate-100/80"
                          }`}
                        />
                      ))}
                    </div>

                    {rows.map((row) => {
                      const incomeHeight = Math.max((Number(row.income || 0) / barMax) * 140, 4);
                      const expensesHeight = Math.max((Number(row.expenses || 0) / barMax) * 140, 4);
                      const paymentsHeight = Math.max((Number(row.payments || 0) / barMax) * 140, 4);

                      return (
                        <div
                          key={row.monthKey}
                          className="group relative z-10 flex min-w-0 flex-1 flex-col items-center gap-2 sm:gap-3"
                        >
                          <div className="flex h-[110px] items-end gap-1 sm:h-[160px] sm:gap-1.5">
                            <div
                              className="w-2 rounded-full bg-emerald-400 transition-all duration-300 hover:bg-emerald-500 sm:w-3"
                              style={{ height: `${incomeHeight}px` }}
                            />
                            <div
                              className="w-2 rounded-full bg-amber-400 transition-all duration-300 hover:bg-amber-500 sm:w-3"
                              style={{ height: `${expensesHeight}px` }}
                            />
                            <div
                              className="w-2 rounded-full bg-blue-400 transition-all duration-300 hover:bg-blue-500 sm:w-3"
                              style={{ height: `${paymentsHeight}px` }}
                            />
                          </div>

                          <div
                            className={`text-[9px] font-extrabold transition-colors sm:text-xs ${
                              theme === "dark"
                                ? "text-slate-400 group-hover:text-slate-200"
                                : "text-slate-500 group-hover:text-slate-800"
                            }`}
                          >
                            {formatLabel(row.monthKey)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={`mt-4 flex flex-wrap items-center justify-center gap-3 text-xs font-bold sm:gap-5 sm:text-sm ${currentTheme.muted}`}>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 sm:h-3 sm:w-3" />
                    {labels.incomeShort}
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-amber-400 sm:h-3 sm:w-3" />
                    {labels.expensesShort}
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-400 sm:h-3 sm:w-3" />
                    {labels.paymentsShort}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div
        className={`flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md sm:rounded-[32px] sm:p-6 ${currentTheme.surface}`}
      >
        <div className="mb-5 flex min-w-0 items-start justify-between gap-3 sm:mb-6">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-black sm:text-2xl">
              {labels.invoicedVsResultTitle}
            </h3>
            <p className={`mt-1 text-sm leading-6 sm:text-base ${currentTheme.muted}`}>
              {labels.invoicedVsResultSubtitle}
            </p>
          </div>
        </div>

        {loading ? (
          <div className={`flex flex-1 items-center justify-center text-sm sm:text-base font-semibold ${currentTheme.muted}`}>
            {labels.loading}
          </div>
        ) : (
          <>
            <div className="mb-5 grid grid-cols-2 gap-3 sm:mb-6">
              <MiniStat
                label={labels.invoiced}
                value={formatMoney(totals.invoiced)}
                tone="slate"
                icon={FileText}
                theme={theme}
              />
              <MiniStat
                label={labels.resultShort}
                value={formatMoney(totals.result)}
                tone={totals.result >= 0 ? "emerald" : "amber"}
                icon={TrendingUp}
                theme={theme}
              />
            </div>

            {!lineMax ? (
              <EmptyChartState
                title={labels.invoicedVsResultTitle}
                description={labels.noChartData}
                icon={LineChart}
                theme={theme}
              />
            ) : (
              <div
                className={`relative mt-auto min-w-0 overflow-hidden rounded-2xl border p-4 pt-5 sm:rounded-3xl sm:p-5 sm:pt-6 ${
                  theme === "dark"
                    ? "border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950"
                    : "border-slate-100 bg-gradient-to-b from-white to-slate-50/50"
                }`}
              >
                <div className="w-full overflow-hidden">
                  <div className="w-full">
                    <svg
                      viewBox={`0 -10 ${lineWidth} ${lineHeight + 40}`}
                      className="h-[170px] w-full overflow-visible sm:h-[210px]"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      {[0, 0.33, 0.66, 1].map((step) => {
                        const y = lineHeight - lineHeight * step;
                        return (
                          <line
                            key={step}
                            x1="0"
                            y1={y}
                            x2={lineWidth}
                            y2={y}
                            stroke={theme === "dark" ? "#1e293b" : "#f1f5f9"}
                            strokeWidth="1.5"
                          />
                        );
                      })}

                      <path d={invoicedPaths.area} fill="url(#invoicedGradient)" opacity="0.3" />
                      <path d={resultPaths.area} fill="url(#resultGradient)" opacity="0.2" />

                      <path
                        d={invoicedPaths.line}
                        fill="none"
                        stroke={theme === "dark" ? "#cbd5e1" : "#334155"}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d={resultPaths.line}
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />

                      {rows.map((row, index) => {
                        const stepX = rows.length > 1 ? lineWidth / (rows.length - 1) : lineWidth;
                        const x = index * stepX;
                        const y1 = lineHeight - (Math.abs(Number(row.invoiced || 0)) / lineMax) * lineHeight;
                        const y2 = lineHeight - (Math.abs(Number(row.result || 0)) / lineMax) * lineHeight;

                        return (
                          <g key={row.monthKey} className="group cursor-pointer">
                            <circle
                              cx={x}
                              cy={y1}
                              r="4"
                              fill={theme === "dark" ? "#0f172a" : "#ffffff"}
                              stroke={theme === "dark" ? "#cbd5e1" : "#334155"}
                              strokeWidth="2"
                              className="transition-all group-hover:r-[6px]"
                            />
                            <circle
                              cx={x}
                              cy={y2}
                              r="4"
                              fill={theme === "dark" ? "#0f172a" : "#ffffff"}
                              stroke="#10b981"
                              strokeWidth="2"
                              className="transition-all group-hover:r-[6px]"
                            />
                            <text
                              x={x}
                              y={lineHeight + 20}
                              textAnchor="middle"
                              fontSize="10"
                              fontWeight="800"
                              fill={theme === "dark" ? "#94a3b8" : "#94a3b8"}
                            >
                              {formatLabel(row.monthKey)}
                            </text>
                          </g>
                        );
                      })}

                      <defs>
                        <linearGradient id="invoicedGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.8" />
                          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient id="resultGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#34d399" stopOpacity="0.8" />
                          <stop offset="100%" stopColor="#6ee7b7" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                </div>

                <div className={`mt-4 flex flex-wrap items-center justify-center gap-4 text-xs font-bold sm:gap-5 sm:text-sm ${currentTheme.muted}`}>
                  <div className="inline-flex items-center gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full sm:h-3 sm:w-3 ${theme === "dark" ? "bg-slate-300" : "bg-slate-700"}`} />
                    {labels.invoiced}
                  </div>
                  <div className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 sm:h-3 sm:w-3" />
                    {labels.resultShort}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
